import { useState } from "react";
import type { EBankingConfig } from "../scenario/types";
import { WindowTitlebar } from "./WindowTitlebar";

/**
 * Online-banking shell (rendered inside the spoofed browser). Two stages:
 *  - "dashboard": the account looks drained + an in-banking alert banner the
 *    learner clicks (the social-engineering hook).
 *  - "verify": a fake "restore access" screen that harvests the phone number,
 *    setting up the inbound vishing call.
 * It never validates anything — it's the attacker's page.
 */
export function EBanking({
  config,
  stage,
  onOpenNotification,
  onSubmitPhone,
}: {
  config: EBankingConfig;
  stage: "dashboard" | "verify";
  onOpenNotification: () => void;
  onSubmitPhone: (value: string) => void;
}) {
  const [phone, setPhone] = useState("");

  return (
    <div className="browser winapp ebank">
      <WindowTitlebar title="Chrome" />
      <div className="chrome">
        <div className="nav">
          <span>{"\u2190"}</span>
          <span>{"\u2192"}</span>
          <span>{"\u21BB"}</span>
        </div>
        <div className="urlbar">
          <span className="lock">{"\u{1F512}"}</span>
          <input value={config.url} readOnly aria-label="Address bar" />
        </div>
      </div>

      <div className="page ebank-page">
        {/* bank top bar */}
        <div className="ebank-top">
          <div className="ebank-brand">
            <span className="ebank-logo">{"\u{1F3E6}"}</span> {config.brand}
          </div>
          <div className="ebank-nav">
            <span className="on">Dashboard</span>
            <span>Accounts</span>
            <span>Transfers</span>
            <span>Payments</span>
          </div>
          <div className="ebank-user">{config.accountName}</div>
        </div>

        {stage === "dashboard" ? (
          <div className="ebank-body">
        <button className="ebank-alert" type="button" onClick={onOpenNotification}>
          <span className="ebank-alert-ico">{"\u26A0"}</span>
          <span className="ebank-alert-text">
            <strong>{config.notification.title}</strong>
            <span>{config.notification.body}</span>
          </span>
          <span className="ebank-alert-go cf-blink-strong">Click here for details</span>
        </button>

            <h2 className="ebank-h">Account Overview</h2>
            <div className="ebank-cards">
              {config.accounts.map((a, i) => (
                <div key={i} className={`ebank-card ${a.danger ? "danger" : ""}`}>
                  <div className="ebank-card-name">{a.name}</div>
                  <div className="ebank-card-bal">{a.balance}</div>
                  <div className="ebank-card-sub">{a.sub ?? a.number}</div>
                </div>
              ))}
            </div>

            {config.transactions && config.transactions.length > 0 && (
              <div className="ebank-tx">
                <div className="ebank-tx-head">Recent transactions</div>
                {config.transactions.map((t, i) => (
                  <div key={i} className="ebank-tx-row">
                    <span className="ebank-tx-date">{t.date}</span>
                    <span className="ebank-tx-desc">
                      {t.desc}
                      {t.tag && <span className="ebank-tx-tag">{t.tag}</span>}
                    </span>
                    <span className={`ebank-tx-amt ${t.negative ? "neg" : ""}`}>{t.amount}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="ebank-verify-wrap">
            <div className="ebank-verify">
              <div className="ebank-verify-ico">{"\u{1F512}"}</div>
              <h2>{config.verify.title}</h2>
              <p>{config.verify.body}</p>
              <label htmlFor="ebank-phone">{config.verify.phoneLabel}</label>
              <input
                id="ebank-phone"
                type="text"
                placeholder={config.verify.phonePlaceholder}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="off"
              />
              <button
                className="btn ebank-verify-cta"
                type="button"
                onClick={() => onSubmitPhone(phone || config.verify.phonePlaceholder)}
              >
                {config.verify.cta}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
